import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, Edit2, Save, X, Sparkles } from 'lucide-react';
import MissingInfoWizard from './MissingInfoWizard';
import QualityGate from './QualityGate';

interface Fact {
    category: string;
    statement: string;
    evidence_refs: string[];
}

interface MissingInfo {
    topic: string;
    question: string;
    impact: string;
    suggested_value?: string;
}

interface SpecData {
    id: string;
    version: number;
    entity_id: string;
    facts: Fact[];
    missing_info: MissingInfo[];
    created_at: string;
    edited_by?: string;
    edit_notes?: string;
    entity_status: string;
}

interface SpecViewProps {
    entityId: string;
    projectId: string;
}

const SpecView: React.FC<SpecViewProps> = ({ entityId, projectId }) => {
    const [spec, setSpec] = useState<SpecData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editedMissingInfo, setEditedMissingInfo] = useState<MissingInfo[]>([]);
    const [editNotes, setEditNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [showWizard, setShowWizard] = useState(false);

    useEffect(() => {
        loadSpec();
    }, [entityId, projectId]);

    const loadSpec = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(
                `/api/spec-review/projects/${projectId}/entities/${entityId}/spec/latest`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setSpec(data);
                setEditedMissingInfo(data.missing_info || []);
            }
        } catch (error) {
            console.error('Error loading spec:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!spec) return;

        try {
            setIsSaving(true);
            const response = await fetch(
                `/api/spec-review/projects/${projectId}/entities/${entityId}/spec`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        facts: spec.facts,
                        missing_info: editedMissingInfo,
                        edit_notes: editNotes
                    })
                }
            );

            if (response.ok) {
                await loadSpec();
                setIsEditing(false);
                setEditNotes('');
            }
        } catch (error) {
            console.error('Error saving spec:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setEditedMissingInfo(spec?.missing_info || []);
        setEditNotes('');
        setIsEditing(false);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!spec) {
        return (
            <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No spec found for this entity</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-white">
                        Specification (v{spec.version})
                    </h3>
                    <p className="text-sm text-slate-400">
                        {spec.facts.length} facts, {spec.missing_info.length} missing info items
                    </p>
                </div>
                <div className="flex gap-2">
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600"
                        >
                            <Edit2 className="w-4 h-4" />
                            Edit
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={handleCancel}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                            >
                                <X className="w-4 h-4" />
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                                <Save className="w-4 h-4" />
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Edit Notes (when editing) */}
            {isEditing && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                        Edit Notes (optional)
                    </label>
                    <textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Describe what you changed..."
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary"
                        rows={2}
                    />
                </div>
            )}

            {/* Quality Gate */}
            <QualityGate
                entityId={entityId}
                projectId={projectId}
                mode="RECO"
            />

            {/* Facts Section */}
            <div>
                <h4 className="text-md font-semibold text-white mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    Facts ({spec.facts.length})
                </h4>
                {spec.facts.length === 0 ? (
                    <p className="text-slate-500 text-sm">No facts detected</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {spec.facts.map((fact, idx) => (
                            <div
                                key={idx}
                                className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                            >
                                <span className="text-xs text-slate-500 uppercase tracking-wide">
                                    {fact.category}
                                </span>
                                <p className="text-white mt-2">{fact.statement}</p>
                                {fact.evidence_refs.length > 0 && (
                                    <div className="mt-2 text-xs text-slate-400">
                                        <span className="font-medium">Evidence:</span>{' '}
                                        {fact.evidence_refs.join(', ')}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Missing Info Section */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-white flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-400" />
                        Missing Information ({editedMissingInfo.length})
                    </h4>
                    {editedMissingInfo.length > 0 && !isEditing && (
                        <button
                            onClick={() => setShowWizard(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:bg-blue-600"
                        >
                            <Sparkles className="w-4 h-4" />
                            Fill with Wizard
                        </button>
                    )}
                </div>
                {editedMissingInfo.length === 0 ? (
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                        <p className="text-green-400 flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5" />
                            No missing information detected
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {editedMissingInfo.map((info, idx) => (
                            <div
                                key={idx}
                                className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="font-semibold text-yellow-400">
                                                {info.topic}
                                            </span>
                                        </div>
                                        <p className="text-slate-300 text-sm mb-2">
                                            {info.question}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            Impact: {info.impact}
                                        </p>
                                        {isEditing && (
                                            <div className="mt-3">
                                                <input
                                                    type="text"
                                                    value={info.suggested_value || ''}
                                                    onChange={(e) => {
                                                        const updated = [...editedMissingInfo];
                                                        updated[idx].suggested_value = e.target.value;
                                                        setEditedMissingInfo(updated);
                                                    }}
                                                    placeholder="Enter value..."
                                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary"
                                                />
                                            </div>
                                        )}
                                        {!isEditing && info.suggested_value && (
                                            <div className="mt-2 text-sm text-green-400">
                                                Suggested: {info.suggested_value}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Metadata */}
            <div className="text-xs text-slate-500 border-t border-slate-800 pt-4">
                <p>Created: {new Date(spec.created_at).toLocaleString()}</p>
                {spec.edited_by && <p>Last edited by: {spec.edited_by}</p>}
                {spec.edit_notes && <p>Notes: {spec.edit_notes}</p>}
            </div>

            {/* Missing Info Wizard — questions are derived from AI-detected missing_info */}
            <MissingInfoWizard
                isOpen={showWizard}
                onClose={() => setShowWizard(false)}
                missingInfo={editedMissingInfo}
                onSave={async (values) => {
                    if (!spec) return;

                    // Convert each wizard answer into a real Fact
                    const wizardFacts = Object.entries(values)
                        .filter(([, v]) => v && v.trim())
                        .map(([key, value]) => ({
                            category: 'context',
                            statement: `${key.replace(/_/g, ' ')}: ${value.trim()}`,
                            evidence_refs: ['wizard-input']
                        }));

                    if (wizardFacts.length === 0) return;

                    // Merge with existing facts (avoid duplicates by key prefix)
                    const existingKeys = new Set(spec.facts.map(f => f.statement.split(':')[0]));
                    const newFacts = wizardFacts.filter(f => !existingKeys.has(f.statement.split(':')[0]));
                    const mergedFacts = [...spec.facts, ...newFacts];

                    // Save to spec immediately
                    try {
                        const token = localStorage.getItem('token');
                        await fetch(
                            `/api/spec-review/projects/${projectId}/entities/${entityId}/spec`,
                            {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                body: JSON.stringify({
                                    facts: mergedFacts,
                                    missing_info: editedMissingInfo,
                                    edit_notes: 'Added via Fill with Wizard'
                                })
                            }
                        );

                        // Trigger spec-review so AI re-analyzes with new context
                        await fetch(
                            `/api/spec-review/projects/${projectId}/entities/${entityId}/spec-review`,
                            {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                            }
                        );

                        // Reload spec to reflect new facts and Quality Gate
                        await loadSpec();
                    } catch (err) {
                        console.error('Error saving wizard data:', err);
                    }
                }}
                projectId={projectId}
            />
        </div>
    );
};

export default SpecView;
